import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import type { WhiteboardLayout } from '../core/WhiteboardLayout';
import type { Grabber, GrabberHub } from '../input/Grabbers';
import type { RoomHub } from '../net/roomHub';
import type { TransformStreamer } from '../net/TransformStreamer';
import type { RoomState } from '../room/RoomState';
import type { RoomView } from '../room/RoomView';
import type { MarkerWidth, PenKind, ToolMode } from './ToolMode';

/**
 * A stroke is a grab: it begins, streams while in progress, and commits exactly once when
 * the pen lifts (plan/whiteboard.md, "A stroke is a grab"). This is that lifecycle, the
 * deliberate mirror of PossessionManager's shape — begin on a deliberate act, track every
 * frame while it continues, commit on release — applied to touch-drawing instead of a grab,
 * and to erasing instead of nothing at all: there is no lock either action needs, because a
 * stroke does not pre-exist before it is drawn and nothing about a stroke is ever held.
 *
 * Reads ToolMode (whether the dominant hand is in draw mode, and which ink is selected) but
 * never writes to it — switching tools and picking a colour stay entirely ToolMode's own
 * business, and neither of them is a Scene concern any more: with no pen ledge, no mesh, and
 * no clone, the tip is a pure function of the grabber's own aim pose (`contactPointFor` below),
 * not a child node read off a held prop. That also retires a whole class of bug this file used
 * to guard against: `PenLedge.contactPoint` needed a forced `computeWorldMatrix` to avoid
 * reading a stale render-id-cached transform (CLAUDE.md, "An unforced computeWorldMatrix can
 * return last frame's answer"); `contactPointFor` reads `Grabber` fields that `GrabberHub`
 * already refreshes before any gesture runs, so there is no cached matrix in the path at all —
 * the staleness bug has nowhere left to hide, not merely a place where it is fixed.
 *
 * **Two ways to draw the pen, one stroke at a time.** plan/whiteboard.md flagged this from the
 * start ("The far path is deferred, not designed out") and named the shape it would take if
 * ever built: the same capture-stream-commit pipeline below, fed points sampled against the
 * board's plane from the dominant grabber's aim ray (`aimOrigin`/`forward`) instead of from its
 * near tip. That is exactly what `remoteBoardPoint` does — aim at the board and pull the
 * dominant trigger, the *far* button (CLAUDE.md, "Grip means near, trigger means far"), and it
 * draws from wherever the ray lands, at any distance the ray reaches. The trigger is free to
 * spend for this: `ConnectionDrawer` already refuses to start a connection while
 * `toolMode.isDrawMode` (plan/history/control-modes.md's own table: "Dominant trigger: *Reserved*" in
 * draw mode), so nothing here competes with it — the two classes read the identical button in
 * mutually exclusive modes, arbitrated by `ToolMode` the way every other draw-mode exclusivity
 * already is.
 *
 * Physical touch always wins over the remote path. `ActiveStroke.origin` records which one is
 * currently driving, and `tickPenDraw` checks the near tip's own distance to the board *first*,
 * on every tick, regardless of what started the current stroke: the instant the tip is
 * genuinely touching, a remote stroke in progress is committed (not discarded — same as every
 * other "something else took priority" ending in this class) and touch takes over. This is what
 * keeps the two paths from ever producing two strokes for the same trigger-and-tip position at
 * once — the failure mode the brief that requested this named directly, and the one most likely
 * to pass a test while wrong: a test that never physically touches while also pulling the
 * trigger cannot see it.
 */

/**
 * Point-to-plane distance, in metres, within which the pen counts as touching the board.
 * Asymmetric like PINCH_ENGAGE/PINCH_RELEASE in Grabbers.ts, and for the same reason: a
 * single threshold would chatter a stroke on and off right at the boundary if the hand's
 * distance from the wall wobbles there. Illustrative numbers straight from
 * plan/whiteboard.md, "Touch to draw" — not measured against real hand tremor, and a
 * starting point for a headset pass to tune, not a settled result.
 *
 * Both are compared against `distanceToBoardPlane`'s *signed* value, and the two sides of
 * the plane are not treated the same way — see that function's own doc for the sign
 * convention, and `tickPenDraw` for exactly where each threshold is spent:
 *
 * - `TOUCH_RELEASE_DISTANCE` only ever bounds the near side, the room the hand reaches in
 *   from. A headset test found a stroke already in progress ending the instant the tip was
 *   pushed *through* the board rather than lifted off it — this class used to compare
 *   `Math.abs(distance)` against this threshold, so the far side's distance growing again
 *   past the board looked identical to the near side's distance growing while lifting off,
 *   and leaning in read exactly like pulling away. There is no equivalent cap on the far
 *   side at all now, deliberately, rather than a larger one: nobody withdraws a marker by
 *   pushing it harder into what it is touching, and every depth a real arm can reach by
 *   leaning or stepping in — there is nothing here to push back, so that depth is bounded
 *   only by the room and the person, not by anything this class knows — is exactly the depth
 *   a finite far-side release threshold would have to guess was "too far to be leaning," with
 *   no measurement behind the guess. The two gates that already exist elsewhere are what stop
 *   a stroke that is genuinely abandoned rather than pushed: losing the grabber or the pen
 *   selection entirely still commits immediately (`tick`'s own fallback), so an unbounded far
 *   side never means an unkillable stroke.
 * - `TOUCH_ENGAGE_DISTANCE` stays symmetric — bounding *both* sides — because starting a
 *   fresh stroke is a different question than continuing one. At this threshold's own 2cm,
 *   a first touch sampled slightly past the plane is not "reaching from behind the wall"; it
 *   is indistinguishable from an ordinary fast tap sampled a moment after the tip crossed it,
 *   exactly as reachable as the same tap sampled a moment before. Guarding engage to the near
 *   side only would not stop anything that actually happens — the pen is always attached to a
 *   hand attached to a body standing in front of the wall, so the tip cannot arrive deep
 *   behind the board without first crossing this same tight window from the front — so it was
 *   left out as a guard against a case this project has no way to produce.
 */
const TOUCH_ENGAGE_DISTANCE = 0.02;
const TOUCH_RELEASE_DISTANCE = 0.04;

/**
 * A new point is worth recording once the tip has moved at least this far since the last
 * one. Matches the ~1mm capture spacing the shipped backend already documents and is tuned
 * against (StrokePointUpdate's remarks, RoomSyncHub.MaxStrokePoints in Tunet.Api) — finer
 * than TransformStreamer's own 2mm POSITION_EPSILON, deliberately: a fixed-distance sample
 * cuts corners on a tight handwriting curl in a way a held node's transform never has to
 * worry about.
 */
const CAPTURE_EPSILON = 0.001;

/**
 * Minimum time between haptic re-pulses while the pen touches the board, in milliseconds.
 * `WebXRAbstractMotionController.pulse` takes a fixed duration and Babylon's own doc says a
 * fresh call "cancel[s] the last pulse call," so a *sustained* rumble is not one long pulse
 * but a short one re-issued on a timer — this is that timer's period, hung off this class's
 * own existing per-frame `tick(nowMs)` (the same clock `tickErase`'s dwell already reads)
 * rather than a new one, per the brief this answers. Sized against `HAPTIC_PULSE_DURATION_MS`
 * below so one pulse is still finishing when the next is due — a gap would read as a stutter,
 * and re-pulsing every single rendered frame (roughly every 11-14ms on Quest) would restart
 * the actuator far more often than a felt "continuous" rumble needs. Untried on hardware, like
 * every other feel constant in this file — a reasoned starting cadence, not a measured one.
 */
const HAPTIC_PULSE_INTERVAL_MS = 80;

/** How long each individual re-pulse lasts. Shorter than the interval above so pulses do not stack. */
const HAPTIC_PULSE_DURATION_MS = 60;

/**
 * Haptic intensity for board contact, 0..1 — `WebXRAbstractMotionController.pulse`'s own
 * range, passed straight through. Was `0.08`, and a first headset pass called that "good,
 * though a little bit buzzy," directing a try at `0.05` next (session 2026-08-03). So this is
 * one hardware observation deep now, not an untuned starting guess — still not a settled value
 * (nobody has tried 0.05 itself on a headset yet), but no longer picked with zero feedback.
 * plan/whiteboard.md, "Haptics on board contact," has the full spike this answers.
 */
const HAPTIC_INTENSITY = 0.05;

/**
 * How close the eraser's near position has to sit to a stroke's own point list to count as
 * touching it. Roughly a real eraser's own half-width, so contact reads as "the eraser's face
 * is over the line" rather than a wide, generous swipe.
 */
const ERASE_CONTACT_RADIUS = 0.05;

/**
 * How long the eraser has to stay on the *same* stroke before it deletes — a short,
 * sub-dwell debounce. plan/whiteboard.md, "Erasing," flags this as the one open feel
 * question in the whole design ("whether contact should delete instantly") and stops short
 * of settling it, while leaning toward recommending some debounce "long enough that a
 * passing brush does not register, short enough that it still reads as touching rather than
 * holding." This picks that reading rather than instant delete, at GRAB_INTENT_MS's own
 * order of magnitude (PossessionManager.ts, "so a passing pinch is not a grab") — a
 * reasoned default, not a settled one; a headset pass should confirm or override it.
 */
const ERASE_DWELL_MS = 120;

/**
 * How far ahead of the aim pose a contact point sits, for a controller — a real marker's
 * rough length for a pen, a real eraser's rough depth for the eraser. No mesh
 * carries these any more (plan/history/control-modes.md, "Pens and the eraser stop existing as world
 * objects"), so there is nothing to measure them off; they are exactly the numbers `PenLedge`
 * used to build a prop at, kept here as the only remaining consumer. See `contactPointFor`.
 */
const PEN_REACH = 0.14;
const ERASER_REACH = 0.05;

/**
 * How far ahead of the aim pose `kind`'s contact point sits — exported so anything else that
 * needs to agree with `contactPointFor`'s own notion of "how far" (M12B's `ToolPropRenderer`,
 * which sizes and positions a rendered pen/eraser from these same numbers) reads it from here
 * rather than keeping a second, independently-tuned copy that could silently drift out of step
 * with the contact point it is supposed to match.
 */
export function reachFor(kind: PenKind): number {
  return kind === 'pen' ? PEN_REACH : ERASER_REACH;
}

/**
 * The pen's or eraser's contact point — the tip that actually has to touch the board — computed
 * directly from the dominant grabber's own pose, not read off a held prop's child node. There is
 * no prop any more: `ToolMode` only ever says *which* ink is selected, never *where* a mesh is,
 * so this function is the entire replacement for `PenLedge.contactPoint` — arithmetic on fields
 * `GrabberHub` already refreshes every frame, not a `TransformNode` whose world matrix could be
 * stale (CLAUDE.md, "An unforced computeWorldMatrix can return last frame's answer" — the bug
 * that forced `PenLedge.contactPoint`'s own getter to call `computeWorldMatrix(true)` has no
 * matrix left to be stale here at all).
 *
 * Branches on `grabber.kind` for the identical reason `PenLedge.followGrabber` used to
 * (CLAUDE.md, "position and aimOrigin are different points, deliberately"): a controller's
 * `aimOrigin` sits near the body, so the tip lands a reach ahead of it: `aimOrigin
 * + forward * reach`. The desktop pointer's `aimOrigin` is the *camera* — `ConnectionDrawer`'s
 * own aim ray needs it that way, to sweep from the eye across the room — so stacking a reach
 * onto it would land a tip centimetres from the eye, nowhere near the cursor. `position` is
 * where a desktop "hand" already is: the point the cursor is over, already sitting on whatever
 * surface it picked. So the pointer's own tip is `position`, exactly, no offset at all.
 *
 * `ToolPropRenderer` (M12B) calls this exact function to place the rendered pen/eraser's tip —
 * this stays the single source of truth for "where the tip is" either way, and the renderer
 * derives a mesh's *pivot* from this function's result rather than the reverse. See that
 * class's own doc for why the dependency has to run in that direction.
 */
export function contactPointFor(grabber: Grabber, kind: PenKind): Vector3 {
  if (grabber.kind === 'pointer') {
    return grabber.position.clone();
  }

  return grabber.aimOrigin.add(grabber.forward.scale(reachFor(kind)));
}

/**
 * How far along the aim ray the board's plane may sit before a remote draw request is refused
 * as "not really aiming at it" — generous, the same order of magnitude as
 * `ConnectionDrawer.MAX_AIM_DISTANCE`, because covering "a larger area" from across the room
 * (the owner's own second reason for asking for this) is the whole point, and a room this
 * product builds is never anywhere near this large.
 */
const REMOTE_DRAW_MAX_DISTANCE = 10;

/**
 * Where the dominant grabber's aim ray — `aimOrigin` and `forward`, the *far* pose
 * (CLAUDE.md, "position and aimOrigin are different points, deliberately") — crosses the
 * whiteboard's own plane, or `null` when it does not land on the board at all: aimed past
 * either edge, aimed parallel to the plane, or aimed away from it entirely (behind the
 * grabber, along the ray's negative direction).
 *
 * Solved directly from `distanceToBoardPlane`'s own signed-distance equation rather than a
 * generic ray/plane intersection: `distanceToBoardPlane(aimOrigin + forward * t, layout)` is
 * linear in `t` (`originDistance + t * dot(forward, normal)`), so setting it to zero and
 * solving for `t` is the entire derivation. `t <= 0` is "the plane sits behind the aim, not in
 * front of it" — the ray does not reach it at all — and is refused rather than mirrored,
 * exactly the "aiming away from the board does not draw" property the brief asked for pinned.
 *
 * Bounds-checked against the rectangle before `clampToBoard` ever runs, deliberately: a point
 * genuinely outside the board must be refused outright, not silently pinned to the nearest
 * edge the way a wandering *touch* point is (`clampToBoard`'s own doc — that clamp exists for
 * a tip that is already touching and strays past the frame while still in contact, not for
 * deciding whether a remote aim counts as "at the board" in the first place). Once accepted,
 * the point is run through `clampToBoard` anyway, purely to pin `z` exactly onto the plane
 * against floating-point drift in the division above — never to rescue a point this function
 * already rejected.
 */
export function remoteBoardPoint(grabber: Grabber, layout: WhiteboardLayout): Vector3 | null {
  const originDistance = distanceToBoardPlane(grabber.aimOrigin, layout);
  const denom = Vector3.Dot(grabber.forward, layout.normal);

  // Aimed (near enough) parallel to the board: never crosses it at all.
  if (Math.abs(denom) < 1e-6) {
    return null;
  }

  const t = -originDistance / denom;

  if (t <= 0 || t > REMOTE_DRAW_MAX_DISTANCE) {
    return null;
  }

  const point = grabber.aimOrigin.add(grabber.forward.scale(t));
  const halfWidth = layout.width / 2;
  const halfHeight = layout.height / 2;

  if (Math.abs(point.x - layout.center.x) > halfWidth || Math.abs(point.y - layout.center.y) > halfHeight) {
    return null;
  }

  return clampToBoard(point, layout);
}

/** Diameter of the small dot shown on the board where a remote stroke would land — a preview,
 *  not the ink itself, so it stays visibly smaller than a drawn line's own on-board presence. */
const AIM_INDICATOR_DIAMETER = 0.035;

/** How far in front of the board's own surface the aim indicator sits, along the board's
 *  normal, toward the room — enough to clear z-fighting with the board mesh behind it. */
const AIM_INDICATOR_OFFSET = 0.01;

type StrokeOrigin = 'touch' | 'remote';

interface ActiveStroke {
  strokeId: string;
  points: Vector3[];

  /** The pen's real ink colour — applied at commit, never while the stroke streams. */
  inkColor: string;

  /** This drawer's own presence colour — what the stroke renders as while it streams. */
  presenceColor: string;

  /** The marker width selected when the stroke began (`ToolMode.markerWidth`) — fixed for the
   *  stroke's whole lifetime, the same "decided at begin, not re-read mid-stroke" treatment
   *  `inkColor` already gets. Sent to the server on commit and rendered locally while the
   *  stroke streams, so a fat stroke already looks fat as it is drawn, not only afterward. */
  width: MarkerWidth;

  /** Which path is currently driving this stroke — read every tick to decide which of the two
   *  gestures gets to continue it, and which has to wait or take over. See the class doc,
   *  "Two ways to draw the pen, one stroke at a time." */
  origin: StrokeOrigin;
}

interface ErasingState {
  strokeId: string;
  since: number;
}

// spec: SPEC-092
// spec: SPEC-093
// spec: SPEC-094
export class DrawingManager {
  private activeStroke: ActiveStroke | null = null;
  private erasing: ErasingState | null = null;

  /**
   * `nowMs` of the last haptic pulse requested, or null when nothing is touching the board.
   * Reset whenever a stroke ends (`endStroke`) so a fresh touch always pulses on its very
   * first tick rather than waiting out whatever interval happened to be left over from a
   * previous, unrelated touch.
   */
  private lastHapticPulseMs: number | null = null;

  /**
   * The small preview dot shown on the board where a remote stroke would land — created once,
   * lazily, on the first tick that needs it, and moved rather than rebuilt on every one after
   * (CLAUDE.md's "one small mesh, created once and moved" brief). `null` until then, and
   * disposed in `dispose()` so a room rebuild cannot leak it.
   */
  private aimIndicatorMesh: Mesh | null = null;
  private aimIndicatorMaterial: StandardMaterial | null = null;

  /** Surfaced to the user on a commit or erase failure; the caller decides how. */
  onNotice: ((message: string) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly state: RoomState,
    private readonly view: RoomView,
    private readonly hub: RoomHub,
    private readonly streamer: TransformStreamer,
    private readonly toolMode: ToolMode,
    private readonly whiteboard: WhiteboardLayout | null,
  ) {}

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /** Whether a stroke is currently being captured — touching, not merely gripped. */
  get isDrawing(): boolean {
    return this.activeStroke !== null;
  }

  tick(nowMs: number): void {
    const grabber = this.grabbers.grabbers.find((candidate) => candidate.role === 'dominant');
    const whiteboard = this.whiteboard;

    // Drawing needs the dominant hand to actually be in draw mode, not merely to have a
    // left-over selection from an earlier session — `ToolMode.selection` deliberately survives
    // a trip back to build mode (plan/history/control-modes.md: "whichever pen you last chose"), so
    // `isDrawMode` is what tells "selected" apart from "in hand right now." Leaving draw mode
    // ends an in-progress stroke exactly like the old "the pen was put down" fallback did —
    // there is no grip to lose any more, but switching tools is the equivalent act.
    const inDrawMode = this.toolMode.isDrawMode;
    const selection = this.toolMode.selection;

    if (grabber && whiteboard && inDrawMode && selection?.kind === 'pen') {
      this.tickPenDraw(grabber, whiteboard, selection.color, nowMs);
    } else {
      this.hideAimIndicator();

      if (this.activeStroke !== null) {
        // The tool switched away from draw, the pen selection was cleared, or the grabber
        // disappeared while a stroke was in progress — commit immediately rather than discard,
        // mirroring the old grip-loss fallback (plan/whiteboard.md: "Losing the grip entirely
        // mid-touch ... commits immediately rather than discarding") generalised from "the grip
        // released" to "draw mode ended," since there is no grip left to lose. Applies to a
        // stroke of either origin identically — neither path has a grip to lose either.
        this.endStroke();
      }
    }

    // The tip is what actually has to touch the board, not the fist holding the pen
    // (plan/whiteboard.md, "Touch to draw") — computed fresh from the grabber's own pose
    // (contactPointFor, above) rather than read off a held prop's child node, since there is no
    // prop any more. Only the near path applies to the eraser — remote drawing is scoped to the
    // pen (the owner's own request was about writing and covering the board, not erasing at a
    // distance), so the eraser's own contact point is exactly what it always was.
    const contactPoint = grabber && inDrawMode && selection ? contactPointFor(grabber, selection.kind) : null;

    if (grabber && selection?.kind === 'eraser' && contactPoint) {
      this.tickErase(contactPoint, nowMs);
    } else {
      this.erasing = null;
    }
  }

  /**
   * Ends whatever is in progress without committing or erasing. Used on teardown
   * (`AuthoringController.dispose`, reachable on every in-app navigate-away, mid-stroke).
   *
   * Deliberately not committing the stroke is correct — a navigate-away is not a pen lift, and
   * there is nothing to commit to. But this used to only clear `activeStroke` itself, leaving
   * both `this.streamer` pointed at a stroke id that will never be committed (so the streamer's
   * own dedupe/pacing state for it never gets torn down) and the live-stroke mesh sitting in
   * `RoomView` forever, since nothing else ever calls `endLiveStroke` for a stroke that was
   * never committed. `endStroke()` performs exactly these two calls on its own (committing)
   * path; do the same pair here; just skip the commit.
   */
  dispose(): void {
    const stroke = this.activeStroke;

    this.activeStroke = null;
    this.erasing = null;
    this.lastHapticPulseMs = null;

    if (stroke !== null) {
      this.streamer.clearStroke();
      this.view.endLiveStroke(stroke.strokeId);
    }

    this.aimIndicatorMaterial?.dispose();
    this.aimIndicatorMesh?.dispose();
    this.aimIndicatorMesh = null;
    this.aimIndicatorMaterial = null;
  }

  /**
   * The pen's own per-tick logic — both ways to draw it. Physical touch is checked first, on
   * every tick, regardless of which path (if either) currently owns `activeStroke`: the instant
   * the tip is genuinely touching the board, that takes over immediately, committing whatever
   * remote stroke might have been in progress rather than letting the two coexist. See the
   * class doc's "Two ways to draw the pen, one stroke at a time."
   */
  private tickPenDraw(grabber: Grabber, whiteboard: WhiteboardLayout, inkColor: string, nowMs: number): void {
    const contactPoint = contactPointFor(grabber, 'pen');

    // Signed, not Math.abs'd — see TOUCH_ENGAGE_DISTANCE/TOUCH_RELEASE_DISTANCE's own comment
    // for why the two states below compare it differently, and distanceToBoardPlane's for the
    // sign convention itself (positive is the near side, the room a hand reaches in from). The
    // release threshold only applies while *this same path* — touch — is what is already
    // driving the stroke; a stroke currently driven remotely has not "touched and released," so
    // starting a fresh touch here is judged by the tighter, symmetric engage threshold, exactly
    // as if nothing were in progress at all.
    const distance = distanceToBoardPlane(contactPoint, whiteboard);
    const nearTouching =
      this.activeStroke?.origin === 'touch'
        ? distance <= TOUCH_RELEASE_DISTANCE
        : Math.abs(distance) <= TOUCH_ENGAGE_DISTANCE;

    if (nearTouching) {
      this.hideAimIndicator();

      if (this.activeStroke !== null && this.activeStroke.origin !== 'touch') {
        // A remote stroke was in progress and the tip just landed on the board for real —
        // commit it now rather than let a physical touch silently extend a remote-origin
        // stroke, or the two ever coexist.
        this.endStroke();
      }

      this.pulseHaptics(grabber, nowMs);

      // Clamped before it is ever recorded, so a hand that strays past the frame's edge
      // while still within touch distance of the plane produces a point pinned to the edge
      // rather than one sitting outside the frame — enforced once, here, rather than
      // downstream (plan/whiteboard.md, "A stroke can never leave the board"). This is also
      // what pins a point pushed *behind* the plane exactly onto it: clampToBoard always
      // overwrites z with the board's own centre z, never contactPoint's own, whichever side
      // of the plane that came from — see clampToBoard's own doc.
      this.continueDrawing('touch', clampToBoard(contactPoint, whiteboard), inkColor);
      return;
    }

    if (this.activeStroke !== null && this.activeStroke.origin === 'touch') {
      // The tip left the plane on the near side — release only ever fires from there once a
      // touch-origin stroke is in progress. A stroke ends here — on contact lost, not on
      // releasing the grip (plan/whiteboard.md: "A stroke ends the instant the tip leaves the
      // plane, not when the grip releases").
      this.endStroke();
    }

    // Remote path: aim at the board and pull the dominant trigger. `remoteBoardPoint` already
    // refuses anything not genuinely aimed at the board's own rectangle, so a `null` here means
    // exactly "aiming away from the board" — no stroke, no indicator, whether or not the
    // trigger happens to be held.
    const remotePoint = remoteBoardPoint(grabber, whiteboard);

    if (remotePoint) {
      this.showAimIndicator(remotePoint, whiteboard, inkColor);
    } else {
      this.hideAimIndicator();
    }

    if (grabber.far.pressed && remotePoint) {
      if (this.activeStroke !== null && this.activeStroke.origin !== 'remote') {
        this.endStroke();
      }

      this.pulseHaptics(grabber, nowMs);
      this.continueDrawing('remote', remotePoint, inkColor);
      return;
    }

    if (this.activeStroke !== null && this.activeStroke.origin === 'remote') {
      // Trigger released, or the aim left the board while it was still held — either way,
      // nothing is left driving a remote stroke.
      this.endStroke();
    }
  }

  /**
   * The capture-stream half of a stroke's lifecycle, shared by both ways to draw it: begins a
   * fresh stroke if none is active, otherwise appends `point` unless it is within
   * `CAPTURE_EPSILON` of the last one recorded. `origin` only matters to `beginStroke` — once a
   * stroke exists, appending a point does not care which path is feeding it, because
   * `tickPenDraw`'s own priority logic above already guarantees only one path calls this at a
   * time for any given `activeStroke`.
   */
  private continueDrawing(origin: StrokeOrigin, point: Vector3, inkColor: string): void {
    if (this.activeStroke === null) {
      this.beginStroke(origin, point, inkColor);
      return;
    }

    const last = this.activeStroke.points[this.activeStroke.points.length - 1]!;

    if (Vector3.DistanceSquared(last, point) < CAPTURE_EPSILON * CAPTURE_EPSILON) {
      return;
    }

    this.activeStroke.points.push(point);
    this.streamer.sampleStrokePoint(this.activeStroke.strokeId, point);
    this.view.updateLiveStroke(
      this.activeStroke.strokeId,
      this.activeStroke.points,
      this.activeStroke.presenceColor,
      this.activeStroke.width,
    );
  }

  /**
   * Re-pulses the dominant grabber's haptic actuator at HAPTIC_PULSE_INTERVAL_MS while the
   * tip touches the board — see that constant's doc for why a sustained rumble means
   * re-pulsing rather than one long pulse, and HAPTIC_INTENSITY's for why this is a spike, not
   * a finished feature. Silently does nothing when `grabber.pulse` is undefined — a tracked
   * hand, the desktop pointer, or a controller with no actuator — the same instinct as
   * CLAUDE.md's rule that nothing may become a *required* WebXR feature: drawing has to work
   * identically whether or not this does anything at all.
   */
  private pulseHaptics(grabber: Grabber, nowMs: number): void {
    if (this.lastHapticPulseMs !== null && nowMs - this.lastHapticPulseMs < HAPTIC_PULSE_INTERVAL_MS) {
      return;
    }

    this.lastHapticPulseMs = nowMs;
    grabber.pulse?.(HAPTIC_INTENSITY, HAPTIC_PULSE_DURATION_MS);
  }

  private beginStroke(origin: StrokeOrigin, point: Vector3, inkColor: string): void {
    const strokeId = crypto.randomUUID();
    const presenceColor = this.state.self?.colorHex ?? '#FFFFFF';
    const width = this.toolMode.markerWidth;

    this.activeStroke = { strokeId, points: [point], inkColor, presenceColor, origin, width };

    this.streamer.sampleStrokePoint(strokeId, point);
    this.view.updateLiveStroke(strokeId, this.activeStroke.points, presenceColor, width);
  }

  private endStroke(): void {
    const stroke = this.activeStroke;

    if (stroke === null) {
      return;
    }

    this.activeStroke = null;
    this.lastHapticPulseMs = null;
    this.streamer.clearStroke();
    this.view.endLiveStroke(stroke.strokeId);

    void this.commit(stroke);
  }

  private async commit(stroke: ActiveStroke): Promise<void> {
    try {
      const committed = await this.hub.commitStroke({
        strokeId: stroke.strokeId,
        color: stroke.inkColor,
        width: stroke.width,
        points: stroke.points.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      });

      // CommitStroke hands back its simplified result rather than broadcasting it to the
      // caller — the same trap CLAUDE.md names directly ("a hub method that returns state
      // does not broadcast it back to you ... awaiting one of those without applying its
      // result is a bug"). Applying it here is what makes this drawer's own board match
      // everyone else's rather than staying crisper, forever, until a reload.
      this.state.upsertStroke(committed);
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * The remote-draw preview: a small dot on the board's own surface at wherever the aim ray
   * would currently land, shown while the pen is selected and the ray is genuinely aimed at
   * the board — so the user sees where a stroke will start before ever pulling the trigger,
   * and keeps seeing it track while one is in progress. Built once, lazily, on first use
   * (`ensureAimIndicator`), then only ever moved and recoloured — never rebuilt — matching the
   * "one small mesh, created once and moved" brief. Coloured to the ink actually selected,
   * rather than a fixed preview colour, so the dot previews what will actually be drawn.
   */
  private ensureAimIndicator(): { mesh: Mesh; material: StandardMaterial } {
    if (this.aimIndicatorMesh && this.aimIndicatorMaterial) {
      return { mesh: this.aimIndicatorMesh, material: this.aimIndicatorMaterial };
    }

    const mesh = CreateSphere(
      'remote-draw-aim-indicator',
      { diameter: AIM_INDICATOR_DIAMETER, segments: 8 },
      this.scene,
    );
    mesh.isPickable = false;
    mesh.setEnabled(false);

    const material = new StandardMaterial('remote-draw-aim-indicator-material', this.scene);
    material.specularColor = Color3.Black();
    material.alpha = 0.85;
    mesh.material = material;

    this.aimIndicatorMesh = mesh;
    this.aimIndicatorMaterial = material;

    return { mesh, material };
  }

  private showAimIndicator(point: Vector3, whiteboard: WhiteboardLayout, inkColor: string): void {
    const { mesh, material } = this.ensureAimIndicator();
    const color = Color3.FromHexString(inkColor);

    material.diffuseColor = color;
    material.emissiveColor = color.scale(0.6);

    // A hair off the board's own surface, toward the room — the same "near side" direction
    // `distanceToBoardPlane` reads as positive — so the dot never z-fights the board mesh it
    // is floating in front of. `layout.normal` already points that way (see that function's
    // own doc): adding it moves toward the room, not into the wall.
    mesh.position.copyFrom(point.add(whiteboard.normal.scale(AIM_INDICATOR_OFFSET)));
    mesh.setEnabled(true);
  }

  private hideAimIndicator(): void {
    this.aimIndicatorMesh?.setEnabled(false);
  }

  private tickErase(contactPoint: Vector3, nowMs: number): void {
    const hitId = this.strokeUnderContact(contactPoint);

    if (hitId === null) {
      this.erasing = null;
      return;
    }

    if (this.erasing === null || this.erasing.strokeId !== hitId) {
      this.erasing = { strokeId: hitId, since: nowMs };
      return;
    }

    if (nowMs - this.erasing.since < ERASE_DWELL_MS) {
      return;
    }

    // Deliberately no "already asked" guard past this point. Contact is sampled every
    // frame, so this can and will fire again for the same id before the first call's
    // removal is visible in local state — DeleteStroke is designed to absorb exactly that
    // as a harmless no-op rather than something a client must avoid triggering (see
    // plan/whiteboard.md, "Erasing," and RoomState.removeStroke).
    void this.eraseStroke(hitId);
  }

  /**
   * Nearest stroke whose own point list passes within ERASE_CONTACT_RADIUS of `point` —
   * the near equivalent of ConnectionDrawer.edgeUnderAim's sampled-distance test, aimed at
   * a stroke's already-recorded points instead of synthetic samples along an edge.
   *
   * Used to return on the *first* stroke found within the radius rather than the nearest one,
   * despite the doc above already promising "nearest" — with two overlapping strokes both
   * within the radius, which one died was `state.strokes`' own Map insertion order, not
   * proximity. Now tracks the closest match across every candidate before returning.
   */
  private strokeUnderContact(point: Vector3): string | null {
    let nearestId: string | null = null;
    let nearestDistance = ERASE_CONTACT_RADIUS;

    for (const stroke of this.state.strokes.values()) {
      const points = stroke.points.map((p) => new Vector3(p.x, p.y, p.z));
      const distance = distanceToPolyline(point, points);

      if (distance <= nearestDistance) {
        nearestId = stroke.id;
        nearestDistance = distance;
      }
    }

    return nearestId;
  }

  private async eraseStroke(strokeId: string): Promise<void> {
    try {
      await this.hub.deleteStroke(strokeId);

      // DeleteStroke's broadcast reaches the whole group including the caller, but this
      // applies the removal locally too rather than only waiting for that self-directed
      // broadcast to come back — the same convention `ConnectionDrawer.removeExisting` already
      // uses for DeleteConnection.
      this.state.removeStroke(strokeId);
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }
}

/**
 * Signed distance from `point` to the board's plane, along its normal. Positive on the
 * side a hand reaches in from (the room); negative on the far side, pushed through toward
 * the wall. The sign is load-bearing, not incidental: `tickPenDraw` reads it directly so that
 * pushing the tip *through* the board while drawing — the far side, growing more negative —
 * cannot be mistaken for lifting it off — the near side, growing more positive — the way
 * `Math.abs`-ing this value once did (see TOUCH_RELEASE_DISTANCE's own comment for the
 * headset-found bug this replaced). The remote-draw path (`remoteBoardPoint`, "The far path is
 * deferred, not designed out" in plan/whiteboard.md, now built) has its own, separate reason to
 * care which side a ray approaches from — this function already reports it either way.
 */
export function distanceToBoardPlane(point: Vector3, layout: WhiteboardLayout): number {
  return Vector3.Dot(point.subtract(layout.center), layout.normal);
}

/**
 * Clamps a room-local point onto the board's own rectangle, pinned exactly onto its plane.
 * The board's normal is always -Z (WhiteboardLayout: "only the north wall is ever the
 * board"), so width runs along local X and height along local Y — a literal Z-pin is exact
 * for that axis-aligned case, not an approximation of a general oriented-plane projection.
 *
 * That z-pin is unconditional: `point.z` is never read, only overwritten with the board's
 * own `layout.center.z`, so a point from *behind* the plane — the tip pushed through the
 * board while drawing, now allowed to keep going indefinitely (TOUCH_RELEASE_DISTANCE's own
 * comment) — projects onto the surface exactly like one still in front of it. Nothing
 * downstream of this function — capture, streaming, storage, rendering — ever sees a point
 * that is not genuinely on the board, regardless of how deep the raw contact point behind it
 * was.
 */
export function clampToBoard(point: Vector3, layout: WhiteboardLayout): Vector3 {
  const halfWidth = layout.width / 2;
  const halfHeight = layout.height / 2;

  return new Vector3(
    clampNumber(point.x, layout.center.x - halfWidth, layout.center.x + halfWidth),
    clampNumber(point.y, layout.center.y - halfHeight, layout.center.y + halfHeight),
    layout.center.z,
  );
}

/**
 * Shortest distance from `point` to the polyline formed by consecutive `points`. Exact
 * point-to-segment distance on each leg, not a resampled approximation — unlike
 * ConnectionDrawer's edgeUnderAim (which interpolates points along an edge because it only
 * ever has two endpoints to work with), a stroke's own point list already contains every
 * point its curvature needs; only a straight run is ever sparse, and a straight run is
 * exactly where the exact segment formula below is cheapest and most correct.
 */
export function distanceToPolyline(point: Vector3, points: Vector3[]): number {
  if (points.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  if (points.length === 1) {
    return Vector3.Distance(point, points[0]!);
  }

  let best = Number.POSITIVE_INFINITY;

  for (let i = 0; i + 1 < points.length; i += 1) {
    const distance = distanceToSegment(point, points[i]!, points[i + 1]!);

    if (distance < best) {
      best = distance;
    }
  }

  return best;
}

function distanceToSegment(point: Vector3, a: Vector3, b: Vector3): number {
  const ab = b.subtract(a);
  const lengthSquared = ab.lengthSquared();

  if (lengthSquared < 1e-12) {
    return Vector3.Distance(point, a);
  }

  const t = Math.min(1, Math.max(0, Vector3.Dot(point.subtract(a), ab) / lengthSquared));
  const closest = a.add(ab.scale(t));

  return Vector3.Distance(point, closest);
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
